import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { buyersApi, type BuyerReferredByDto, type ReferralBroker } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, DetailField, FormField, Input, StatusBadge, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import {
  CounterpartyPicker,
  type SelectedCounterparty,
} from "@/components/portfolioAdmin/loanDetail/parties/CounterpartyPicker";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import {
  DEFAULT_REFERRAL_FEE_PERCENT,
  formatReferralFee,
  referralFeeProblem,
} from "@/lib/referralDisclosure";

export const SUGGESTION_CAPTION = "Suggested from the buyer's prequalification site.";

/** One read, shared by the Attribution row and the header badge. */
function useReferredBy(buyerId: string) {
  return useQuery({
    queryKey: buyerKeys.referredBy(buyerId),
    queryFn: () => buyersApi.getReferredBy(buyerId),
  });
}

function brokerName(b: ReferralBroker): string {
  return b.printedName ?? b.companyName;
}

/** A server-side broker as the picker's controlled value. */
function toSelected(b: ReferralBroker): SelectedCounterparty {
  return {
    id: b.id,
    name: brokerPickerLabel(b).primary,
    detail: [b.name, b.status].filter(Boolean).join(" · ") + (b.isArchived ? " · archived" : ""),
    data: b,
  };
}

/**
 * D4 — the buyer's "Referred by": the broker who introduced them and the
 * co-broker fee the cover letter's Referral Disclosure prints. A row of the
 * Attribution card; the edit opens a modal only when the server says this
 * caller may write it (`canEdit`, AND-ed with the host's own gate).
 */
export function BuyerReferredBy({ buyerId, canEdit = true }: { buyerId: string; canEdit?: boolean }) {
  const q = useReferredBy(buyerId);
  const [editing, setEditing] = useState(false);
  const data = q.data;
  const broker = data?.broker ?? null;

  return (
    <>
      <DetailField
        label="Referred by"
        data-testid="buyer-referred-by"
        value={
          data === undefined ? undefined : (
            <span className="inline-flex flex-wrap items-center gap-space-2">
              {broker ? (
                <span data-testid="buyer-referred-by-value">
                  <Link to={`/brokers/${broker.id}`} className="text-gold-shadow hover:underline">
                    {brokerName(broker)}
                  </Link>
                  {broker.isArchived && <span className="text-gray-mid"> (archived)</span>}
                  {data.feePercent != null && ` · Co-broker fee ${formatReferralFee(data.feePercent)}`}
                </span>
              ) : (
                <span className="text-gray-mid" data-testid="buyer-referred-by-value">
                  Not set
                </span>
              )}
              {canEdit && data.canEdit && (
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => setEditing(true)}
                  aria-label={broker ? "Edit Referred by" : "Set Referred by"}
                  data-testid="buyer-referred-by-edit"
                >
                  <Pencil size={14} strokeWidth={1.5} aria-hidden /> {broker ? "Edit" : "Set"}
                </Button>
              )}
            </span>
          )
        }
      />
      {editing && data !== undefined && (
        <ReferredByModal buyerId={buyerId} data={data} onClose={() => setEditing(false)} />
      )}
    </>
  );
}

function ReferredByModal({
  buyerId,
  data,
  onClose,
}: {
  buyerId: string;
  data: BuyerReferredByDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  // D9 — with nothing saved, the prequal site's owner PRE-FILLS the picker.
  // Nothing is written until Save.
  const suggested = data.broker === null ? data.suggestedBroker : null;
  const [picked, setPicked] = useState<SelectedCounterparty | null>(() => {
    const initial = data.broker ?? suggested;
    return initial ? toSelected(initial) : null;
  });
  const [feeText, setFeeText] = useState(String(data.feePercent ?? DEFAULT_REFERRAL_FEE_PERCENT));

  const fee = feeText.trim() === "" ? null : Number(feeText);
  const feeError = referralFeeProblem(fee);

  const save = useMutation({
    mutationFn: (args: { brokerId: string | null; feePercent: number | null }) =>
      buyersApi.setReferredBy(buyerId, args.brokerId, args.feePercent),
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      // Package states derive their disclosure from the buyer default.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(args.brokerId === null ? "Referred by cleared." : "Referred by saved.");
      onClose();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  const showSuggestion = suggested !== null && picked?.id === suggested.id;

  return (
    <Modal
      open
      onClose={onClose}
      title="Referred by"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" type="button" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          {data.broker !== null && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate({ brokerId: null, feePercent: null })}
              data-testid="buyer-referred-by-clear"
            >
              Clear
            </Button>
          )}
          <Button
            size="sm"
            type="button"
            disabled={save.isPending || picked === null || feeError !== null}
            onClick={() => picked && fee !== null && save.mutate({ brokerId: picked.id, feePercent: fee })}
            data-testid="buyer-referred-by-save"
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <div>
          <CounterpartyPicker
            counterpartyType="ReferringBroker"
            noun="broker"
            label="Referring broker"
            value={picked}
            onChange={setPicked}
          />
          {showSuggestion && (
            <p className="m-0 mt-space-1 text-caption text-gray-mid" data-testid="buyer-referred-by-suggestion">
              {SUGGESTION_CAPTION}
            </p>
          )}
        </div>
        <FormField label="Co-broker fee %" error={feeError ?? undefined}>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={feeText}
            onChange={(e) => setFeeText(e.target.value)}
            data-testid="buyer-referred-by-fee"
          />
        </FormField>
      </div>
    </Modal>
  );
}

/** The header chip — only when a broker is set. */
export function ReferredByBadge({ buyerId }: { buyerId: string }) {
  const broker = useReferredBy(buyerId).data?.broker ?? null;
  if (broker === null) return null;
  return (
    <Link to={`/brokers/${broker.id}`} className="hover:underline" data-testid="buyer-referred-by-badge">
      <StatusBadge tone="neutral">Referred by {brokerName(broker)}</StatusBadge>
    </Link>
  );
}
